import {
  FaUsers, FaCommentDots, FaEye, FaStar, FaHeart, FaMapMarkerAlt,
  FaGraduationCap, FaBriefcase, FaSearch, FaArrowRight, FaUser,
  FaSpinner, FaCheck, FaGem, FaCopy, FaWhatsapp, FaShareAlt, FaGift,
  FaPhone, FaRocket, FaCrown, FaChartBar
} from 'react-icons/fa';
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  getUserDashboard, getShortlist, getInterests,
  addToShortlist, sendInterest, getRechargeStats,
  getNotifications, getUserReferralInfo, getUserActivePlans,
  getBoostedProfiles,
} from '../../Api/CoreApi';
import { handleShortlistUser, fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import { message } from 'antd';

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [statsData, setStatsData] = useState([]);
  const [moreMatches, setMoreMatches] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [summaryItems, setSummaryItems] = useState([]);
  const [shortlistedIds, setShortlistedIds] = useState([]);
  const [interestSentIds, setInterestSentIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [rechargeStats, setRechargeStats] = useState({
    totalPlans: 0, totalAmount: 0, activePlans: 0, expiredPlans: 0
  });
  const [referralLoading, setReferralLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [viewedContacts, setViewedContacts] = useState([]);
  const [boostedIds, setBoostedIds] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [referralData, setReferralData] = useState(0);

  useEffect(() => {
    fetchDashboard();
    fetchUserInteractions();
    fetchRechargeStats();
    fetchNotifications();
    fetchUserPlans();
    fetchBoostedProfiles();

    const unsubscribe = addShortlistListener((newIds) => {
      setShortlistedIds(newIds);
    });
    return () => unsubscribe();
  }, []);

  const fetchReferralInfo = async (userId) => {
    if (!userId) return;
    setReferralLoading(true);
    try {
      const data = await getUserReferralInfo(userId);
      if (data && !data.error) setReferralData(data);
    } catch (error) {
      console.error("Referral fetch error:", error);
    } finally {
      setReferralLoading(false);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const success = document.execCommand('copy');
      if (success) {
        setCopied(true);
        message.success("✅ Referral link copied!");
        setTimeout(() => setCopied(false), 2000);
      } else {
        message.error("❌ Copy failed. Please copy manually.");
      }
    } catch (err) {
      message.error("❌ Copy not supported.");
    }
    document.body.removeChild(textarea);
  };


  const handleCopyLink = (text) => {
    let copyText = text;
    if (
      referralData?.refer &&
      !text.includes('?ref=') &&
      !text.includes('&ref=')
    ) {
      copyText = `${text}?ref=${referralData.refer}`;
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(copyText)
        .then(() => {
          setCopied(true);
          message.success("✅ Referral link copied!");
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => fallbackCopy(copyText));
    } else {
      fallbackCopy(copyText);
    }
  };

  const handleWhatsAppShare = (link, code) => {
    const shareLink = link.includes('?ref=') ? link : `${link}?ref=${code}`;
    const text = `🎉 Join me on Rishtey!\n\nUse my referral code: *${code}*\n\n👇 Register here:\n${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const getNotificationIcon = (type) => {
    if (!type) return '🔔';
    const icons = {
      'profile_view': '👀', 'new_profile': '🆕',
      'connection_request': '💖', 'request_accepted': '✅',
      'request_rejected': '❌', 'new_plan': '📦',
      'message': '💬', 'shortlist': '⭐',
      'like': '❤️', 'general': '🔔'
    };
    return icons[type] || '🔔';
  };

  const getDisplayId = (user) => {
    if (!user) return 'N/A';
    if (user.profile_id) return `${user.profile_id}`;
    if (user.user_id || user.id) return user.user_id || user.id;
    return 'N/A';
  };

  const canViewContact = (userId) => {
    if (viewedContacts.includes(userId)) return true;
    const contactPlan = activePlans.find(
      p => p.plan_type === 'contact_view' && p.remaining_views > 0
    );
    return !!contactPlan;
  };

  const hasProfileBoost = () => activePlans.some(p => p.plan_type === 'profile_boost');

  const fetchRechargeStats = async () => {
    try {
      const stats = await getRechargeStats();
      setRechargeStats(stats);
    } catch (err) {
      console.error("Recharge stats error:", err);
    }
  };

  const fetchUserPlans = async () => {
    try {
      const data = await getUserActivePlans();
      console.log('📦 Active Plans Response:', data);

      // ✅ Handle new format properly
      const plansArray = data?.plans || [];
      const viewedContactsArray = data?.viewed_contacts || [];

      setActivePlans(plansArray);
      setViewedContacts(viewedContactsArray);

      console.log(`✅ ${plansArray.length} active plans loaded`);
    } catch (err) {
      console.error("Plans error:", err);
      setActivePlans([]);
      setViewedContacts([]);
    }
  };

  const fetchBoostedProfiles = async () => {
    try {
      const data = await getBoostedProfiles();
      setBoostedIds(data?.boosted_user_ids || []);
    } catch (err) {
      console.error("Boost error:", err);
    }
  };


  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserDashboard();
      if (!data || data.error) {
        setError(data?.error || "Failed to load dashboard");
        setLoading(false);
        return;
      }
      if (!data.currentUser) {
        setError("Please complete your profile.");
        setLoading(false);
        return;
      }

      const user = data.currentUser || {};
      setUserData({
        name: user.username || user.firstname || "User",
        displayId: getDisplayId(user),
        userId: user.user_id || user.id,
        age: user.age || 0,
        height: user.height || "N/A",
        education: user.education || user.course || "N/A",
        occupation: user.occupation || user.job_title || "N/A",
        location: user.location || `${user.city || ""}, ${user.state || ""}` || "N/A",
        image: user.image || `https://ui-avatars.com/api/?name=${user.username || "User"}&background=ff0055&color=fff&size=128`,
        profile_id: user.profile_id,
        // ⭐ NEW: Store religion + caste for filtering
        religion: user.religion || '',
        caste: user.caste || user.cast || '',
      });

      const userId = user.user_id || user.id;
      if (userId) fetchReferralInfo(userId);

      // ⭐ Get current user religion + caste for matching
      const userReligion = (user.religion || '').toLowerCase().trim();
      const userCaste = (user.caste || user.cast || '').toLowerCase().trim();

      const match = data.topMatch || {};

      // ⭐ Check top match religion + caste match
      if (match && match.User_id) {
        const topReligion = (match.religion || '').toLowerCase().trim();
        const topCaste = (match.caste || match.cast || '').toLowerCase().trim();

        const religionMatch = !topReligion ? true : userReligion === topReligion;
        const casteMatch = userCaste && topCaste ? userCaste === topCaste : false;

        // ⭐ Only set if religion AND caste match
        if (religionMatch && casteMatch) {
          setMatchData({
            userId: match.User_id || match.user_id || match.id,
            displayId: getDisplayId(match),
            name: match.name || match.firstname || "Match",
            age: match.age || 0,
            height: match.height || "N/A",
            education: match.education || match.course || "N/A",
            location: match.location || `${match.city || ""}, ${match.state || ""}` || "N/A",
            image: match.image || `https://ui-avatars.com/api/?name=Match&background=ff4d88&color=fff&size=128`,
            matchPercentage: match.matchPercentage || 75,
            online: match.online || false,
            profile_id: match.profile_id,
          });
        } else {
          setMatchData(null);
        }
      }

      setSummaryItems(data.matchSummary || []);

      const stats = data.stats || {};
      setStatsData([
        { label: "Profile Views", value: stats.views || 0, icon: FaEye, gradient: "linear-gradient(135deg, #ff0055, #ff4d88)" },
        { label: "Interests", value: stats.interests || 0, icon: FaHeart, gradient: "linear-gradient(135deg, #ff4d88, #ff80aa)" },
        { label: "Messages", value: stats.messages || 0, icon: FaCommentDots, gradient: "linear-gradient(135deg, #c2185b, #ff0055)" },
        { label: "Matches", value: stats.matches || 0, icon: FaUsers, gradient: "linear-gradient(135deg, #ff80aa, #ffb3c8)" }
      ]);

      const matches = data.moreMatches || [];

      const processedMatches = matches
        .map(item => ({
          userId: item.User_id || item.user_id || item.id,
          displayId: getDisplayId(item),
          name: item.name || item.firstname || "Match",
          age: item.age || 0,
          height: item.height || "N/A",
          education: item.education || item.course || "N/A",
          location: item.location || `${item.city || ""}, ${item.state || ""}` || "N/A",
          image: item.image || `https://ui-avatars.com/api/?name=Match&background=ff0055&color=fff&size=128`,
          match: item.matchPercentage || 75,
          profile_id: item.profile_id,
          isBoosted: boostedIds.includes(item.User_id || item.user_id || item.id),
          // ⭐ Store religion + caste for filtering
          religion: item.religion || '',
          caste: item.caste || item.cast || '',
        }))
        // ⭐ FILTER: Only same religion + caste
        .filter(m => {
          const mReligion = (m.religion || '').toLowerCase().trim();
          const mCaste = (m.caste || '').toLowerCase().trim();

          // Religion must match (or empty)
          const religionMatch = !mReligion ? true : userReligion === mReligion;

          // Caste MUST match
          const casteMatch = userCaste && mCaste ? userCaste === mCaste : false;

          return religionMatch && casteMatch;
        });

      // ⭐ Sort: Boosted first
      processedMatches.sort((a, b) => {
        if (a.isBoosted && !b.isBoosted) return -1;
        if (!a.isBoosted && b.isBoosted) return 1;
        return 0;
      });

      console.log(`✅ Filtered ${processedMatches.length} matches (Religion: ${userReligion}, Caste: ${userCaste})`);
      setMoreMatches(processedMatches);

    } catch (err) {
      setError(err.message || "Failed to load dashboard");
      message.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInteractions = async () => {
    try {
      const shortlisted = await fetchShortlistedUsers();
      setShortlistedIds(shortlisted || []);
      const interests = await getInterests();
      setInterestSentIds(interests.map(item => item.to_user));

    } catch (err) {
      console.error("Interactions error:", err);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const data = await getNotifications(10);
      let activityItems = [];

      if (data?.notifications?.length > 0) {
        activityItems = data.notifications.map((item, index) => ({
          id: item.id || index + 1,
          icon: item.icon || getNotificationIcon(item.notification_type) || '🔔',
          text: item.message || item.title || 'New notification',
          time: item.time_ago || 'Just now',
          isRead: item.is_read || false,
          link: item.link || '#',
          notification_type: item.notification_type || 'general',
          from_user_id: item.from_user_id || null
        }));
      } else {
        activityItems = [
          { id: 1, icon: '👀', text: 'Someone viewed your profile', time: '2 hrs ago', link: '/visitors', isRead: true },
          { id: 2, icon: '💖', text: 'New interest received', time: '4 hrs ago', link: '/requests', isRead: true }
        ];
      }
      setActivities(activityItems);
    } catch (error) {
      setActivities([
        { id: 1, icon: '👀', text: 'Someone viewed your profile', time: '2 hrs ago', link: '/visitors' },
        { id: 2, icon: '💖', text: 'New interest received', time: '4 hrs ago', link: '/requests' }
      ]);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleSendInterest = async (userId) => {
    if (!userId) { message.error("Invalid user"); return; }
    setActionLoading(prev => ({ ...prev, [userId]: 'interest' }));
    try {
      const response = await sendInterest(userId);
      if (response && response.id) setInterestSentIds(prev => [...prev, userId]);
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleShortlist = async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'shortlist' }));
    const success = await handleShortlistUser(userId, setShortlistedIds);
    setActionLoading(prev => ({ ...prev, [userId]: null }));
    return success;
  };


  // LOADING STATE
  if (loading) {
    return (
      <>
        <style>{`@keyframes dbSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: '60vh', flexDirection: 'column', gap: 16, padding: '20px'
        }}>
          <FaSpinner size={40} style={{ color: '#ff0055', animation: 'dbSpin 1s linear infinite' }} />
          <p style={{ color: '#888', fontSize: 13, textAlign: 'center' }}>💖 Loading your dashboard...</p>
        </div>
      </>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '60vh', padding: '16px'
      }}>
        <div style={{
          background: '#fff', border: '2px solid #ffe4ec', borderRadius: 20,
          padding: '30px 20px', width: '100%', maxWidth: 500, textAlign: 'center'
        }}>
          <h2 style={{ color: '#ff0055', marginBottom: 12 }}>⚠️ Error</h2>
          <p style={{ color: '#888', fontSize: 13 }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20, padding: '11px 30px',
              background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
              color: '#fff', border: 'none', borderRadius: 999,
              fontWeight: 700, cursor: 'pointer', fontSize: 13
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const planTypeConfig = {
    contact_view: { icon: '📱', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: 'Contact View Plan' },
    specific_user: { icon: '👤', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', label: 'Special Plan' },
    profile_boost: { icon: '🚀', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', label: 'Profile Boost' },
    custom: { icon: '⚙️', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', label: 'Custom Plan' },
  };

  // MAIN RENDER
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes dbSpin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes dbShimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes dbPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.015)} }
        @keyframes dbSlideIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes dbFadeIn { from{opacity:0} to{opacity:1} }

        .db-page {
          background: linear-gradient(180deg, #fff 0%, #fffafb 50%, #fff5f7 100%);
          min-height: 100vh;
          padding: 20px 16px;
        }
        .db-wrap {
          max-width: 1360px;
          margin: 0 auto;
          animation: dbFadeIn .5s ease;
        }

        /* ===== WELCOME ===== */
        .db-welcome {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 22px 24px;
          border-radius: 20px;
          background: linear-gradient(135deg, #fff0f3 0%, #ffe4ec 50%, #ffd3df 100%);
          border: 2px solid #ffd3df;
          box-shadow: 0 8px 28px rgba(255,0,85,.08);
          gap: 16px;
          flex-wrap: wrap;
          animation: dbSlideIn .4s ease;
        }
        .db-welcome-left { flex: 1; min-width: 200px; }
        .db-welcome h1 {
          font-size: 22px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0;
          line-height: 1.35;
        }
        .db-welcome p {
          color: #888;
          margin: 6px 0 0;
          font-size: 13px;
          font-weight: 500;
        }
        .db-find-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 22px;
          border-radius: 999px;
          border: none;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          font-weight: 700;
          font-size: 13.5px;
          cursor: pointer;
          box-shadow: 0 6px 22px rgba(255,0,85,.25);
          transition: all .3s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .db-find-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(255,0,85,.3);
        }
        .db-find-btn:active { transform: translateY(0); }

        /* ===== CARD ===== */
        .db-card {
          background: #fff;
          border-radius: 20px;
          border: 2px solid #ffe4ec;
          box-shadow: 0 4px 20px rgba(255,0,85,.04);
          padding: 20px;
          transition: all .3s ease;
          position: relative;
          overflow: hidden;
        }
        .db-card:hover {
          border-color: #ffb3c8;
          box-shadow: 0 12px 40px rgba(255,0,85,.08);
        }
        .db-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #ff0055, #ff4d88, #ff80aa, #ff0055);
          background-size: 200% 100%;
          animation: dbShimmer 3s linear infinite;
        }

        /* ===== STATS GRID ===== */
        .db-grid-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-bottom: 20px;
        }
        .db-stat-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 16px;
          animation: dbSlideIn .5s ease;
        }
        .db-stat-left { min-width: 0; flex: 1; }
        .db-stat-label {
          font-size: 11.5px;
          color: #999;
          margin: 0;
          font-weight: 600;
          letter-spacing: 0.3px;
        }
        .db-stat-value {
          font-size: 26px;
          font-weight: 800;
          margin: 4px 0 0;
          color: #1a0a10;
          line-height: 1.1;
        }
        .db-stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0,0,0,.1);
        }

        /* ===== BOOST BANNER ===== */
        .db-boost-banner {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border: 2px solid #fde68a;
          border-radius: 18px;
          margin-bottom: 16px;
          animation: dbPulse 3s ease infinite;
          flex-wrap: wrap;
        }
        .db-boost-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #ffd700, #ffb300);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(255,215,0,.3);
        }
        .db-boost-text { flex: 1; min-width: 120px; }
        .db-boost-text h4 { font-size: 13.5px; font-weight: 800; color: #92400e; margin: 0 0 3px; }
        .db-boost-text p { font-size: 11.5px; color: #b45309; margin: 0; font-weight: 500; }
        .db-boost-badge {
          margin-left: auto; flex-shrink: 0;
          padding: 6px 14px;
          background: linear-gradient(135deg, #ffd700, #ffb300);
          color: #1a0a10; border-radius: 999px;
          font-size: 11px; font-weight: 800;
          box-shadow: 0 3px 10px rgba(255,215,0,.3);
        }

        /* ===== CONTACT BANNER ===== */
        .db-contact-banner {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 2px solid #bfdbfe;
          border-radius: 18px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .db-contact-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6, #60a5fa);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(59,130,246,.25);
        }
        .db-contact-text { flex: 1; min-width: 120px; }
        .db-contact-text h4 { font-size: 13.5px; font-weight: 800; color: #1e40af; margin: 0 0 3px; }
        .db-contact-text p { font-size: 11.5px; color: #3b82f6; margin: 0; font-weight: 600; }
        .db-contact-remaining { margin-left: auto; text-align: center; flex-shrink: 0; }
        .db-contact-remaining-num {
          font-size: 28px; font-weight: 900; color: #3b82f6; line-height: 1;
        }
        .db-contact-remaining-label { font-size: 9px; color: #93c5fd; font-weight: 700; letter-spacing: 1px; }

        /* ===== SECTION HEADER ===== */
        .db-sec-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          padding-bottom: 14px;
          border-bottom: 2px solid #fff0f3;
          gap: 10px;
          flex-wrap: wrap;
        }
        .db-sec-title {
          font-size: 17px;
          font-weight: 800;
          color: #1a0a10;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .db-view-all {
          color: #ff0055;
          font-size: 12.5px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: all .2s;
          padding: 6px 12px;
          border-radius: 999px;
        }
        .db-view-all:hover {
          background: #fff0f3;
        }

        /* ===== PLANS SECTION ===== */
        .db-plans-section { margin-bottom: 20px; }
        .db-grid-plans {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 14px;
          margin-top: 16px;
        }
        .db-plan-card {
          border-radius: 16px;
          padding: 18px;
          border: 2px solid;
          transition: all .3s ease;
        }
        .db-plan-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,.06);
        }
        .db-plan-type-tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 10.5px;
          font-weight: 700;
          margin-bottom: 10px;
        }
        .db-plan-name { font-size: 14.5px; font-weight: 800; color: #1a0a10; margin: 0 0 12px; }
        .db-progress-bar-wrap {
          width: 100%;
          height: 8px;
          border-radius: 999px;
          overflow: hidden;
          margin-bottom: 6px;
        }
        .db-progress-bar {
          height: 100%;
          border-radius: 999px;
          transition: width .6s ease;
        }
        .db-progress-label {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          margin-bottom: 8px;
        }
        .db-plan-expiry {
          font-size: 10.5px;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 8px;
        }

        /* ===== MATCHES + ACTIVITY GRID ===== */
        .db-grid-bottom {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        /* ===== MATCH CARDS GRID ===== */
        .db-grid-matches {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 14px;
          margin-top: 16px;
        }
        .db-match-item {
          background: linear-gradient(135deg, #fffafc, #fff0f3);
          border-radius: 18px;
          padding: 16px 12px;
          text-align: center;
          border: 1.5px solid #ffe4ec;
          position: relative;
          overflow: hidden;
          transition: all .3s ease;
          animation: dbSlideIn .5s ease;
        }
        .db-match-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(255,0,85,.1);
          border-color: #ffb3c8;
        }
        .db-match-item.boosted {
          border-color: #ffd700;
          box-shadow: 0 4px 16px rgba(255,215,0,.18);
        }
        .db-match-item.boosted:hover {
          box-shadow: 0 10px 30px rgba(255,215,0,.25);
        }
        .db-match-item-img-wrap {
          display: inline-block;
          padding: 3px;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          border-radius: 50%;
          margin-bottom: 10px;
        }
        .db-match-item-img {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          border: 3px solid #fff;
          display: block;
          object-fit: cover;
        }
        .db-match-item-name {
          font-size: 13.5px;
          font-weight: 800;
          color: #1a0a10;
          margin: 4px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .db-match-item-detail {
          font-size: 11px;
          color: #888;
          margin: 3px 0;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .db-match-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          margin-top: 6px;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 3px 8px rgba(255,0,85,.2);
        }
        .db-boost-tag {
          position: absolute;
          top: 8px; right: 8px;
          padding: 3px 8px;
          background: linear-gradient(135deg, #ffd700, #ffb300);
          color: #1a0a10;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 800;
          box-shadow: 0 2px 6px rgba(255,215,0,.3);
        }
        .db-match-item-btn {
          width: 100%;
          height: 34px;
          border-radius: 10px;
          border: 1.5px solid #ff0055;
          background: #fff;
          color: #ff0055;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          margin-top: 8px;
          transition: all .25s;
        }
        .db-match-item-btn:hover {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 4px 12px rgba(255,0,85,.2);
        }

        /* ===== ACTIVITY ===== */
        .db-activity-list { list-style: none; padding: 0; margin: 14px 0 0; }
        .db-activity-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 8px;
          border-bottom: 1px solid #fff0f3;
          cursor: pointer;
          transition: all .2s;
          border-radius: 10px;
        }
        .db-activity-item:hover {
          background: #fff8fa;
        }
        .db-activity-item:last-child { border-bottom: none; }
        .db-activity-icon {
          width: 38px;
          height: 38px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          background: linear-gradient(135deg, #fff0f3, #ffe4ec);
          flex-shrink: 0;
        }
        .db-activity-text {
          flex: 1;
          font-size: 12.5px;
          color: #555;
          margin: 0;
          font-weight: 500;
          line-height: 1.45;
          min-width: 0;
        }
        .db-activity-time {
          font-size: 10px;
          color: #bbb;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .db-empty { text-align: center; padding: 36px 16px; }
        .db-empty p { color: #aaa; margin: 0; font-size: 13px; font-weight: 500; }

        /* ===== REFERRAL CARD ===== */
        .db-referral-card {
          background: linear-gradient(135deg, #fff0f3 0%, #ffe4ec 50%, #ffd3df 100%);
          border: 2px solid #ffd3df;
          border-radius: 22px;
          padding: 24px;
          margin-bottom: 20px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 6px 24px rgba(255,0,85,.06);
        }
        .db-referral-title {
          font-size: 17px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0 0 18px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .db-referral-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-bottom: 16px;
        }
        .db-referral-box {
          background: #fff;
          border-radius: 16px;
          padding: 16px 18px;
          border: 1.5px solid #ffd3df;
          transition: all .3s;
        }
        .db-referral-box:hover {
          box-shadow: 0 6px 20px rgba(255,0,85,.08);
        }
        .db-referral-box-label {
          font-size: 10.5px;
          color: #ff4d88;
          font-weight: 700;
          text-transform: uppercase;
          margin: 0 0 6px;
          letter-spacing: 0.5px;
        }
        .db-referral-box-value {
          font-size: 24px;
          font-weight: 900;
          color: #ff0055;
          margin: 0;
          letter-spacing: 2px;
          word-break: break-all;
        }
        .db-referral-link-box {
          background: #fff;
          border-radius: 14px;
          padding: 14px 16px;
          border: 1.5px solid #ffd3df;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }
        .db-referral-link-text {
          font-size: 11.5px;
          color: #888;
          font-weight: 600;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }
        .db-referral-btns {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .db-referral-btn {
          height: 44px;
          border-radius: 12px;
          border: none;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          transition: all .25s;
        }
        .db-referral-btn:hover { transform: translateY(-1px); }
        .db-referral-btn-copy {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 4px 12px rgba(255,0,85,.2);
        }
        .db-referral-btn-wa {
          background: linear-gradient(135deg, #25d366, #128c7e);
          color: #fff;
          box-shadow: 0 4px 12px rgba(37,211,102,.2);
        }
        .db-copy-code-btn {
          margin-top: 10px;
          padding: 7px 16px;
          border: 1.5px solid #ff0055;
          border-radius: 999px;
          background: #fff;
          color: #ff0055;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: all .25s;
        }
        .db-copy-code-btn:hover {
          background: #ff0055;
          color: #fff;
        }
        .db-view-referred-btn {
          margin-top: 10px;
          padding: 8px 18px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 4px 12px rgba(255,0,85,.25);
          transition: all .25s;
        }
        .db-view-referred-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(255,0,85,.3);
        }
        .db-referral-link-copy-btn {
          padding: 8px 16px;
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          border: none;
          border-radius: 10px;
          color: #fff;
          font-size: 11.5px;
          font-weight: 700;
          cursor: pointer;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: all .25s;
        }
        .db-referral-link-copy-btn:hover {
          transform: translateY(-1px);
        }
       

        /* =======================================================
           DESKTOP LARGE - 1281px+
           ======================================================= */

        /* =======================================================
           TABLET LARGE - 1025px to 1280px
           ======================================================= */
        @media (max-width: 1280px) {
          .db-grid-stats { grid-template-columns: repeat(4, 1fr); gap: 12px; }
          .db-grid-matches { grid-template-columns: repeat(3, 1fr); }
          .db-grid-bottom { gap: 16px; }
        }

        /* =======================================================
           TABLET - 769px to 1024px
           ======================================================= */
        @media (max-width: 1024px) {
          .db-page { padding: 16px 14px; }
          .db-grid-stats { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .db-grid-bottom { grid-template-columns: 1fr; gap: 16px; }
          .db-grid-matches { grid-template-columns: repeat(3, 1fr); gap: 12px; }
          .db-grid-plans { grid-template-columns: repeat(2, 1fr); }
          .db-welcome h1 { font-size: 20px; }
          .db-welcome p { font-size: 12.5px; }
          .db-stat-value { font-size: 24px; }
        }

        /* =======================================================
           MOBILE LARGE - 601px to 768px
           ======================================================= */
        @media (max-width: 768px) {
          .db-page { padding: 14px 12px; }

          .db-welcome {
            padding: 16px 18px;
            gap: 12px;
            border-radius: 16px;
            flex-direction: column;
            align-items: flex-start;
          }
          .db-welcome-left { min-width: 0; width: 100%; }
          .db-welcome h1 { font-size: 18px; }
          .db-welcome p { font-size: 12px; }
          .db-find-btn {
            padding: 10px 18px;
            font-size: 12.5px;
            width: 100%;
            justify-content: center;
          }

          .db-grid-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-bottom: 16px;
          }
          .db-stat-card { padding: 14px 12px; }
          .db-stat-value { font-size: 22px; }
          .db-stat-label { font-size: 10.5px; }
          .db-stat-icon { width: 42px; height: 42px; border-radius: 12px; }

          .db-grid-bottom { grid-template-columns: 1fr; gap: 14px; }

          .db-grid-matches {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          .db-match-item { padding: 14px 10px; border-radius: 16px; }
          .db-match-item-img { width: 62px; height: 62px; }
          .db-match-item-name { font-size: 12.5px; }
          .db-match-item-detail { font-size: 10.5px; }
          .db-match-badge { font-size: 9.5px; padding: 3px 10px; }
          .db-match-item-btn { height: 32px; font-size: 11px; }

          .db-grid-plans { grid-template-columns: 1fr; }

          .db-referral-btns { grid-template-columns: 1fr 1fr; }
          .db-referral-btn { height: 42px; font-size: 12px; }

          .db-boost-banner { padding: 13px 16px; border-radius: 16px; }
          .db-contact-banner { padding: 13px 16px; border-radius: 16px; }
          .db-boost-text h4 { font-size: 12.5px; }
          .db-boost-text p { font-size: 11px; }
          .db-contact-text h4 { font-size: 12.5px; }
          .db-contact-text p { font-size: 11px; }
          .db-contact-remaining-num { font-size: 24px; }

          .db-sec-title { font-size: 15px; }
          .db-view-all { font-size: 11.5px; }

          .db-card { padding: 16px; border-radius: 18px; }
          .db-referral-card { padding: 18px; border-radius: 18px; }
          .db-referral-title { font-size: 15px; }
        }

        /* =======================================================
           MOBILE - 481px to 600px
           ======================================================= */
        @media (max-width: 600px) {
          .db-page { padding: 12px 10px; }

          .db-welcome {
            padding: 14px 16px;
            border-radius: 14px;
          }
          .db-welcome h1 { font-size: 17px; }

          .db-referral-grid { grid-template-columns: 1fr; gap: 10px; }
          .db-referral-box { padding: 14px 16px; }
          .db-referral-box-value { font-size: 20px; }

          .db-referral-link-box {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .db-referral-link-text {
            white-space: normal;
            word-break: break-all;
            font-size: 11px;
          }
          .db-referral-link-copy-btn {
            width: 100%;
            justify-content: center;
            padding: 10px;
          }

          .db-referral-btns { grid-template-columns: 1fr; gap: 8px; }

          .db-grid-matches {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          .db-match-item-img { width: 56px; height: 56px; }

          .db-contact-remaining-num { font-size: 22px; }

          .db-activity-item { padding: 10px 6px; gap: 10px; }
          .db-activity-icon { width: 34px; height: 34px; font-size: 13px; border-radius: 10px; }
          .db-activity-text { font-size: 12px; }
          .db-activity-time { font-size: 9.5px; }
        }

        /* =======================================================
           MOBILE SMALL - 401px to 480px
           ======================================================= */
        @media (max-width: 480px) {
          .db-page { padding: 10px 8px; }

          .db-welcome {
            padding: 12px 14px;
            gap: 10px;
          }
          .db-welcome h1 { font-size: 16px; }
          .db-welcome p { font-size: 11px; }
          .db-find-btn { padding: 9px 16px; font-size: 12px; }

          .db-grid-stats { gap: 8px; margin-bottom: 14px; }
          .db-stat-card { padding: 12px 10px; }
          .db-stat-value { font-size: 20px; }
          .db-stat-label { font-size: 10px; }
          .db-stat-icon { width: 38px; height: 38px; border-radius: 10px; }
          .db-stat-icon svg { width: 15px; height: 15px; }

          .db-grid-matches {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          .db-match-item { padding: 12px 8px; border-radius: 14px; }
          .db-match-item-img { width: 52px; height: 52px; }
          .db-match-item-name { font-size: 12px; }
          .db-match-item-detail { font-size: 10px; }
          .db-match-badge { font-size: 9px; padding: 3px 8px; }
          .db-match-item-btn { height: 30px; font-size: 10.5px; border-radius: 8px; }

          .db-card { padding: 14px 12px; border-radius: 16px; }

          .db-referral-card { padding: 14px 12px; border-radius: 16px; }
          .db-referral-title { font-size: 14px; gap: 6px; }
          .db-referral-box { padding: 12px 14px; border-radius: 14px; }
          .db-referral-box-value { font-size: 18px; letter-spacing: 1.5px; }
          .db-referral-btn { height: 40px; font-size: 11.5px; border-radius: 10px; }

          .db-boost-banner { padding: 11px 14px; gap: 10px; border-radius: 14px; }
          .db-boost-icon { width: 38px; height: 38px; font-size: 16px; }
          .db-boost-text h4 { font-size: 12px; }
          .db-boost-text p { font-size: 10px; }
          .db-boost-badge { padding: 4px 10px; font-size: 10px; }

          .db-contact-banner { padding: 11px 14px; gap: 10px; border-radius: 14px; }
          .db-contact-icon { width: 38px; height: 38px; }
          .db-contact-text h4 { font-size: 12px; }
          .db-contact-text p { font-size: 10px; }
          .db-contact-remaining-num { font-size: 20px; }

          .db-sec-header { padding-bottom: 10px; }
          .db-sec-title { font-size: 14px; }
          .db-view-all { font-size: 11px; padding: 5px 10px; }

          .db-plan-card { padding: 14px; border-radius: 14px; }
          .db-plan-name { font-size: 13px; }
          .db-plan-type-tag { font-size: 10px; padding: 3px 10px; }
        }

        /* =======================================================
           MOBILE EXTRA SMALL - max 360px
           ======================================================= */
        @media (max-width: 360px) {
          .db-page { padding: 8px 6px; }

          .db-welcome {
            padding: 10px 12px;
            border-radius: 12px;
          }
          .db-welcome h1 { font-size: 14.5px; }
          .db-welcome p { font-size: 10.5px; }
          .db-find-btn { padding: 8px 14px; font-size: 11px; }

          .db-grid-stats {
            grid-template-columns: 1fr 1fr;
            gap: 6px;
          }
          .db-stat-card { padding: 10px 8px; border-radius: 14px; }
          .db-stat-value { font-size: 18px; }
          .db-stat-label { font-size: 9px; }
          .db-stat-icon { width: 34px; height: 34px; border-radius: 9px; }

          .db-grid-matches {
            grid-template-columns: 1fr;
            gap: 10px;
          }
          .db-match-item {
            display: flex;
            flex-direction: row;
            align-items: center;
            text-align: left;
            padding: 12px;
            gap: 12px;
            border-radius: 14px;
          }
          .db-match-item-img-wrap { margin-bottom: 0; flex-shrink: 0; }
          .db-match-item-img { width: 56px; height: 56px; }
          .db-match-item-info { flex: 1; min-width: 0; }
          .db-match-item-name { font-size: 13px; text-align: left; }
          .db-match-item-detail { font-size: 10.5px; text-align: left; }
          .db-match-badge { font-size: 9px; }
          .db-match-item-btn {
            width: auto;
            padding: 0 14px;
            height: 30px;
            font-size: 10px;
            margin-top: 6px;
          }
          .db-boost-tag { top: 6px; right: 6px; font-size: 8px; padding: 2px 6px; }

          .db-card { padding: 12px 10px; border-radius: 14px; }

          .db-referral-card { padding: 12px 10px; border-radius: 14px; }
          .db-referral-title { font-size: 13px; margin-bottom: 12px; }
          .db-referral-box { padding: 10px 12px; border-radius: 12px; }
          .db-referral-box-label { font-size: 9.5px; }
          .db-referral-box-value { font-size: 16px; letter-spacing: 1px; }
          .db-copy-code-btn { font-size: 10px; padding: 6px 12px; }
          .db-view-referred-btn { font-size: 10.5px; padding: 7px 14px; }
          .db-referral-btn { height: 38px; font-size: 11px; }

          .db-boost-banner { padding: 10px 12px; border-radius: 12px; }
          .db-boost-icon { width: 34px; height: 34px; font-size: 14px; border-radius: 9px; }
          .db-boost-text h4 { font-size: 11px; }
          .db-boost-text p { font-size: 9.5px; }
          .db-boost-badge { font-size: 9px; padding: 3px 8px; }

          .db-contact-banner { padding: 10px 12px; border-radius: 12px; }
          .db-contact-icon { width: 34px; height: 34px; border-radius: 9px; }
          .db-contact-text h4 { font-size: 11px; }
          .db-contact-text p { font-size: 9.5px; }
          .db-contact-remaining-num { font-size: 18px; }
          .db-contact-remaining-label { font-size: 8px; }

          .db-sec-title { font-size: 13px; }
          .db-view-all { font-size: 10.5px; }

          .db-activity-item { padding: 8px 4px; gap: 8px; }
          .db-activity-icon { width: 30px; height: 30px; font-size: 12px; border-radius: 8px; }
          .db-activity-text { font-size: 11px; }
          .db-activity-time { font-size: 9px; }

          .db-plan-card { padding: 12px; border-radius: 12px; }
          .db-plan-name { font-size: 12.5px; }
          .db-plan-type-tag { font-size: 9.5px; }
          .db-plan-expiry { font-size: 9.5px; }
          .db-progress-label { font-size: 10px; }

          .db-empty { padding: 24px 12px; }
          .db-empty p { font-size: 11.5px; }
        }
      `}</style>

      <div className="db-page">
        <div className="db-wrap">

          {/* ===== WELCOME ===== */}
          <div className="db-welcome">
            <div className="db-welcome-left">
              <h1>Welcome back, {userData?.name?.split(' ')[0] || "User"} 💖</h1>
              <p>Here's what's happening with your matches today</p>
            </div>
            <button className="db-find-btn" onClick={() => navigate('/all-users')}>
              <FaSearch size={13} /> Find Matches
            </button>
          </div>

          {/* ===== STATS ===== */}
          <div className="db-grid-stats">
            {statsData.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="db-card db-stat-card" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <div className="db-stat-left">
                    <p className="db-stat-label">{stat.label}</p>
                    <p className="db-stat-value">{stat.value}</p>
                  </div>
                  <div className="db-stat-icon" style={{ background: stat.gradient }}>
                    <Icon size={18} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== PROFILE BOOST BANNER ===== */}
          {hasProfileBoost() && (
            <div className="db-boost-banner">
              <div className="db-boost-icon">🚀</div>
              <div className="db-boost-text">
                <h4>Your Profile Boost is Active!</h4>
                <p>Your profile is showing at top position</p>
              </div>
              <div className="db-boost-badge">⭐ BOOSTED</div>
            </div>
          )}

          {/* ===== CONTACT PLAN BANNER - Multiple Plans Support ===== */}
          {activePlans.filter(p => p.plan_type === 'contact_view').length > 0 && (() => {
            // ⭐ Combine ALL contact view plans
            const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');

            // Calculate total remaining across all contact plans
            const totalRemaining = contactPlans.reduce((sum, p) => {
              if (p.contact_view_limit >= 9999) return 999999;
              return sum + (p.remaining_views || 0);
            }, 0);

            const isUnlimited = contactPlans.some(p => p.contact_view_limit >= 9999);
            const displayRemaining = isUnlimited ? '∞' : totalRemaining;

            const totalUsed = contactPlans.reduce((sum, p) => sum + (p.contacts_viewed || 0), 0);
            const totalLimit = contactPlans.reduce((sum, p) => {
              if (p.contact_view_limit >= 9999) return 999999;
              return sum + (p.contact_view_limit || 0);
            }, 0);

            return (
              <div className="db-contact-banner">
                <div className="db-contact-icon"><FaPhone size={16} /></div>
                <div className="db-contact-text">
                  <h4>
                    Contact View Plan Active
                    {contactPlans.length > 1 && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, background: '#3b82f6',
                        color: '#fff', padding: '2px 8px', borderRadius: 999,
                        fontWeight: 700
                      }}>
                        {contactPlans.length} PLANS
                      </span>
                    )}
                  </h4>
                  <p>
                    {displayRemaining} contact views remaining
                    {!isUnlimited && ` (Used: ${totalUsed} / ${totalLimit})`}
                  </p>
                </div>
                <div className="db-contact-remaining">
                  <div className="db-contact-remaining-num">{displayRemaining}</div>
                  <div className="db-contact-remaining-label">REMAINING</div>
                </div>
              </div>
            );
          })()}

          {/* ===== REFERRAL SECTION ===== */}
          <div className="db-referral-card">
            <h3 className="db-referral-title">
              <FaGift color="#ff0055" /> 🎁 Your Referral Program
            </h3>

            {referralLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <FaSpinner size={22} style={{ animation: 'dbSpin 1s linear infinite', color: '#ff0055' }} />
              </div>
            ) : referralData ? (
              <>
                <div className="db-referral-grid">
                  <div className="db-referral-box">
                    <p className="db-referral-box-label">🎯 Your Referral Code</p>
                    <p className="db-referral-box-value">
                      {referralData.referral_code || referralData.refer || "N/A"}
                    </p>
                    <button
                      className="db-copy-code-btn"
                      onClick={() => handleCopyLink(String(referralData.referral_code || referralData.refer))}
                    >
                      <FaCopy size={10} /> {copied ? "Copied! ✅" : "Copy Code"}
                    </button>
                  </div>

                  <div className="db-referral-box">
                    <p className="db-referral-box-label">👥 Total Referred Users</p>
                    <p className="db-referral-box-value">{referralData.total_referred_users || 0}</p>
                    <p style={{ fontSize: 11, color: '#888', margin: '8px 0 0', fontWeight: 600 }}>
                      {referralData.total_referred_users > 0
                        ? `🎉 You've referred ${referralData.total_referred_users} people!`
                        : "Share your link to start referring!"}
                    </p>
                    {referralData.total_referred_users > 0 && (
                      <button
                        className="db-view-referred-btn"
                        onClick={() => navigate('/my-referrals')}
                      >
                        View All Referred Users
                      </button>
                    )}
                  </div>
                </div>

                {referralData.referral_link && (
                  <>
                    <div className="db-referral-link-box">
                      <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                        <p style={{
                          fontSize: 10, color: '#ff4d88', fontWeight: 700,
                          margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5
                        }}>
                          🔗 Your Referral Link
                        </p>
                        <p className="db-referral-link-text">{referralData.referral_link}</p>
                      </div>
                      <button
                        className="db-referral-link-copy-btn"
                        onClick={() => handleCopyLink(referralData.referral_link)}
                      >
                        <FaCopy size={11} /> Copy
                      </button>
                    </div>
                    <button
                      className="db-referral-btn db-referral-btn-wa"
                      onClick={() => handleWhatsAppShare(referralData.referral_link, referralData.referral_code || referralData.refer)}
                    >
                      <FaWhatsapp size={14} /> Share on WhatsApp
                    </button>

                  </>
                )}
              </>
            ) : (
              <div className="db-empty">
                <p>⚠️ Referral info not available</p>
                <button
                  onClick={() => fetchReferralInfo(userData?.userId)}
                  style={{
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                    border: 'none', borderRadius: 999,
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', marginTop: 12
                  }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* ===== ACTIVE PLANS ===== */}
          {activePlans.length > 0 && (
            <div className="db-plans-section">
              {/* More Matches */}
              <div className="db-card">
                <div className="db-sec-header">
                  <h3 className="db-sec-title">
                    <FaHeart color="#ff0055" size={14} /> More Matches
                    {moreMatches.length > 0 && (
                      <span style={{
                        fontSize: 11, padding: '3px 10px',
                        background: '#fff0f3', color: '#ff0055',
                        borderRadius: 999, border: '1px solid #ffd3df',
                        fontWeight: 700
                      }}>
                        {moreMatches.length} matched
                      </span>
                    )}
                  </h3>
                  <button className="db-view-all" onClick={() => navigate('/all-users')}>
                    View All <FaArrowRight size={10} />
                  </button>
                </div>

                {/* ⭐ Info Banner - Show filter criteria */}
                {userData?.caste && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                    border: '1.5px solid #ffd3df',
                    borderRadius: 10,
                    padding: '9px 14px',
                    marginBottom: 14,
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 7
                  }}>
                    <span style={{ fontSize: 11.5, color: '#c2185b', fontWeight: 700 }}>
                      🎯 Same {userData.religion || 'Religion'} + {userData.caste} Profiles
                      {boostedIds.length > 0 && (
                        <span style={{ marginLeft: 6, color: '#f59e0b', fontSize: 10 }}>
                          🚀 Boosted first
                        </span>
                      )}
                    </span>
                    <span style={{
                      fontSize: 10, background: '#fff', padding: '3px 9px',
                      borderRadius: 999, border: '1px solid #ffd3df', color: '#888'
                    }}>
                      {moreMatches.length} profiles
                    </span>
                  </div>
                )}

                {moreMatches.length > 0 ? (
                  <div className="db-grid-matches">
                    {moreMatches.map((m, idx) => {
                      const isBoosted = boostedIds.includes(m.userId) || m.isBoosted;
                      return (
                        <div
                          key={m.displayId}
                          className={`db-match-item ${isBoosted ? 'boosted' : ''}`}
                          style={{ animationDelay: `${idx * 0.08}s` }}
                        >
                          {isBoosted && <div className="db-boost-tag">🚀 Boosted</div>}
                          <div className="db-match-item-img-wrap">
                            <img src={m.image} alt={m.name} className="db-match-item-img" />
                          </div>
                          <div className="db-match-item-info">
                            <h4 className="db-match-item-name">{m.name}</h4>
                            <p className="db-match-item-detail" style={{ color: '#ff4d88', fontWeight: 700, fontSize: 10 }}>
                              ID: {m.displayId}
                            </p>
                            <p className="db-match-item-detail">{m.age} Yrs, {m.height}</p>
                            <p className="db-match-item-detail">🎓 {m.education}</p>
                            <p className="db-match-item-detail">📍 {m.location}</p>

                            {/* ⭐ Show Caste badge */}
                            {m.caste && (
                              <div style={{
                                fontSize: 9.5, color: '#2e7d32', fontWeight: 700,
                                padding: '2px 8px',
                                background: '#e8f5e9',
                                border: '1px solid #4caf50',
                                borderRadius: 999,
                                marginTop: 4, marginBottom: 4,
                                display: 'inline-block'
                              }}>
                                🙏 {m.caste} ✓
                              </div>
                            )}

                            <span className="db-match-badge">💖 {m.match}%</span>
                            <button
                              className="db-match-item-btn"
                              onClick={() => navigate(`/user-profile/${m.userId}`)}
                            >
                              View Profile
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="db-empty" style={{ padding: '40px 20px' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>💔</div>
                    <p style={{ fontSize: 15, fontWeight: 800, color: '#c2185b', marginBottom: 7 }}>
                      No Perfect Matches Found
                    </p>
                    <p style={{ fontSize: 11.5, color: '#aaa', marginBottom: 14, lineHeight: 1.6 }}>
                      No profile with same religion and caste available right now
                    </p>
                    <button
                      onClick={() => navigate('/all-users')}
                      style={{
                        padding: '9px 22px',
                        background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                        color: '#fff', border: 'none', borderRadius: 999,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer'
                      }}
                    >
                      View All Profiles
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== MORE MATCHES + ACTIVITY ===== */}


          {/* Activity */}
          <div className="db-card">
            <div className="db-sec-header">
              <h3 className="db-sec-title">💫 Activity</h3>
              <button className="db-view-all" onClick={() => navigate('/notifications')}>
                View All <FaArrowRight size={10} />
              </button>
            </div>

            {notificationLoading ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <FaSpinner size={22} style={{ animation: 'dbSpin 1s linear infinite', color: '#ff0055' }} />
              </div>
            ) : activities.length > 0 ? (
              <ul className="db-activity-list">
                {activities.slice(0, 6).map((a, i) => (
                  <li
                    key={a.id || i}
                    className="db-activity-item"
                    onClick={() => a.link && a.link !== '#' && navigate(a.link)}
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <div className="db-activity-icon">{a.icon || '📌'}</div>
                    <p className="db-activity-text">{a.text}</p>
                    <span className="db-activity-time">{a.time}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="db-empty"><p>No recent activity</p></div>
            )}
          </div>


        </div>
      </div>
    </>
  );
};

export default Dashboard;